import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectPreferredScaleSet } from '@epicuro-next/state/user-state';
import { isNonNullish, numericSort } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { Product, ScaleSets } from 'graphql-api/gql-api-interfaces';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../configurations';

import { selectSizesForCurrentDeliveryPeriod } from './config-strip.selectors';
import { selectScaleSets } from './data/config-strip.data.selectors';

function getAvailableSizeIndexes(
  scaleSets: ScaleSets,
  sizeProducts: Partial<Product>[],
) {
  // build indexes of the sizes with inventory
  const availableSizeIndexes = new Set<number>();

  const defaultScaleSet = scaleSets.scales[scaleSets.default];
  const [mainSizeAttribute] = Object.keys(defaultScaleSet);

  const sizes = defaultScaleSet[mainSizeAttribute];

  for (const product of sizeProducts) {
    const available = product?.inventoryItems?.some(
      (inv) => inv.quantity || inv.quantity === null,
    );
    const productSize = product?.attributes?.[mainSizeAttribute] as string;
    if (available && productSize) {
      availableSizeIndexes.add(sizes.indexOf(productSize));
    }
    if (availableSizeIndexes.size === sizes.length) {
      break;
    }
  }

  // fill gaps between unavailable sizes, for instance: 2,5,6 should show sizes in the middle but not edges and should become 2,3,4,5,6
  const availableIndexesArray =
    Array.from(availableSizeIndexes).sort(numericSort);
  return new Set(
    availableIndexesArray.reduce((a, b) => {
      const prev = a[a.length - 1];
      while (b >= 0 && prev && prev - b > 1) {
        a.push(++b);
      }
      return a;
    }, availableIndexesArray),
  );
}

export const selectAvailableSizeIndexes = createSelector(
  selectScaleSets,
  selectSizesForCurrentDeliveryPeriod,
  (scaleSets, sizeProducts) => {
    if (scaleSets === null) {
      return new Set();
    }

    return getAvailableSizeIndexes(
      scaleSets,
      sizeProducts as Partial<Product>[],
    );
  },
);

export const selectAvailableScaleSets = (preserveScales = false) =>
  createSelector(
    selectScaleSets,
    selectAvailableSizeIndexes,
    (scaleSets, availableSizeIndexes) => {
      if (scaleSets === null) {
        return null;
      }
      const defaultScaleSet = scaleSets.scales[scaleSets.default];

      const [mainSizeAttribute, secondarySizeAttribute] =
        Object.keys(defaultScaleSet);

      if (
        !scaleSets.scales[scaleSets.default][mainSizeAttribute]?.filter(Boolean)
          ?.length
      ) {
        return null;
      }

      const availableScaleSets: ScaleSets = {
        default: scaleSets.default,
        scales: {},
      };

      Object.keys(scaleSets.scales).forEach((scale) => {
        const scaleSet = scaleSets.scales[scale];
        availableScaleSets.scales[scale] = {
          [mainSizeAttribute]: scaleSet[mainSizeAttribute]?.filter(
            (_, index) => availableSizeIndexes.has(index) || preserveScales,
          ),
        };
        if (secondarySizeAttribute) {
          availableScaleSets.scales[scale][secondarySizeAttribute] =
            scaleSet[secondarySizeAttribute];
        }
      });

      const filteredAvailableScales = Object.keys(
        availableScaleSets.scales,
      ).filter((scaleKey) => {
        return (
          availableScaleSets.scales[scaleKey][mainSizeAttribute].length > 0
        );
      });

      if (filteredAvailableScales.length === 0) {
        return null;
      }

      return availableScaleSets;
    },
  );

export const selectLockSizeRunSelector = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.LockSizeRunSelector,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.LockSizeRunSelector, ''),
  (lockSizeRunSelector) => {
    return lockSizeRunSelector?.length ? lockSizeRunSelector : undefined;
  },
);

export const selectHasLockSizeRunSelector = createSelector(
  selectScaleSets,
  selectLockSizeRunSelector,
  (scaleSets, lockSizeRun) => {
    if (lockSizeRun) {
      return isNonNullish(scaleSets?.scales[lockSizeRun as string]);
    }
  },
);

export const selectSelectedScaleOrDefaultAvailable = createSelector(
  selectPreferredScaleSet,
  selectScaleSets,
  selectHasLockSizeRunSelector,
  selectLockSizeRunSelector,
  (selected, scaleSets, hasLockSizeRun, lockSizeRun) => {
    if (hasLockSizeRun) {
      return lockSizeRun;
    }
    if (selected && scaleSets?.scales[selected]) {
      return selected;
    }
    return scaleSets?.default;
  },
);
